import type { DocumentNode, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Button, Chip, Link, Typography } from '@mui/material';
import type { Updater } from '@tanstack/react-table';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isEmpty } from 'lodash';
import type { MRT_ColumnDef, MRT_PaginationState } from 'material-react-table';
import MaterialReactTable from 'material-react-table';
import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import { JOB_STATE_COLORS } from 'src/constants';
import { getJobResults } from 'src/dynamoQueries/jobs';
import JsonNotes from 'src/Jobs/JsonNotes';
import theme from 'src/theme';
import styles from './JobsTableStyles';

// Type definitions for Job data structure
interface JobUser {
  id: string;
  email: string;
  username: string;
}

interface JobAction {
  id: string;
  title: string;
  type: string;
}

interface JobFormat {
  id: string;
  filename: string;
  mimetype: string;
}

interface ContentObject {
  id: string;
  name: string;
}

interface JobItem {
  id: string;
  contentObject: ContentObject | null;
}

export interface Job {
  id: string;
  user: JobUser;
  status: string;
  statusMsg: string;
  createdTimestamp: string;
  modifiedTimestamp: string;
  action: JobAction;
  metadata: string;
  format: JobFormat | null;
  item: JobItem | null;
}

interface JobsTableProps {
  jobs: Job[];
  title: string;
  pagination: MRT_PaginationState;
  onPaginationChange: (updater: Updater<MRT_PaginationState>) => void;
  totalCount: number;
}

interface JobResultMutationResponse {
  getJobResult: {
    result: string;
  };
}

interface DateFormatOptions {
  year: 'numeric' | '2-digit';
  month: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day: 'numeric' | '2-digit';
  hour: 'numeric' | '2-digit';
  minute: 'numeric' | '2-digit';
  second: 'numeric' | '2-digit';
}

const JobsTable = ({ jobs, title, pagination, onPaginationChange, totalCount }: JobsTableProps): ReactElement => {
  const classes = styles();

  const buildJobResultsMutation = (): DocumentNode => {
    const m = getJobResults();
    const gMutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(gMutation);
  };

  const [getResults, { data, error }] = useMutation<JobResultMutationResponse>(buildJobResultsMutation());

  const getJobResultsById = (jobId: string): Promise<FetchResult<JobResultMutationResponse>> => {
    return getResults({ variables: { jobId: jobId } });
  };

  useEffect(() => {
    if (data && !error) {
      const uri = data.getJobResult?.result;
      window.open(uri, '_top');
    } else if (error) {
      console.log('A job result download error occurred', error);
      alert(error);
    }
  }, [data, error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: getJobResultsById is a stable mutation function
  const tableColumns: MRT_ColumnDef<Job>[] = useMemo(
    () => [
      {
        accessorFn: (row: Job): string => {
          if (row.createdTimestamp) {
            const options: DateFormatOptions = {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
            };
            return new Date(row.createdTimestamp).toLocaleString('en-US', options);
          }
          return '';
        },
        header: 'Job Start Time',
      },
      {
        accessorFn: (row: Job): string => {
          if (row.modifiedTimestamp) {
            const options: DateFormatOptions = {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
            };
            return new Date(row.modifiedTimestamp).toLocaleString('en-US', options);
          }
          return '';
        },
        header: 'Last Updated',
      },
      {
        header: 'Source',
        Cell: ({ cell }): ReactElement => {
          const job = cell.row.original;
          const assetId = job.item?.contentObject?.id;
          const assetName = job.item?.contentObject?.name;
          if (assetId && assetName) {
            return (
              <Link className={classes.link} onClick={() => window.open(`/home/media-page/${assetId}`, '_blank')}>
                {assetName}
              </Link>
            );
          } else {
            return <span></span>;
          }
        },
      },
      {
        accessorKey: 'user.username',
        header: 'Owner',
        maxSize: 120,
      },
      {
        accessorKey: 'action.title',
        header: 'Job Type',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell }): ReactElement => {
          const job = cell.row.original;
          const color = JOB_STATE_COLORS[job.status] || 'gray';
          return <Chip className={classes.status} label={job.status} style={{ backgroundColor: color }} />;
        },
      },
      {
        header: 'Result',
        maxSize: 120,
        Cell: ({ cell }): ReactElement => {
          const job = cell.row.original;
          if (job.status === 'complete' && job.action.type === 'MSCLIP' && !isEmpty(JSON.parse(job.metadata))) {
            return (
              <Button
                className={classes.tableButton}
                onClick={() => {
                  getJobResultsById(job.id);
                }}
              >
                {'Download'}
              </Button>
            );
          }
          return <span></span>;
        },
      },
      {
        accessorKey: 'metadata',
        header: 'Notes',
        Cell: ({ cell }): ReactElement => {
          const notes = JSON.parse(cell.row.original.metadata) as Record<string, string | number | boolean>;
          return <JsonNotes notes={notes} />;
        },
      },
    ],
    [classes.link, classes.status, classes.tableButton],
  );

  return (
    <div className={classes.outerContainer}>
      <Typography variant="h2" className={classes.title}>
        {title}
      </Typography>
      <MaterialReactTable
        columns={tableColumns}
        data={jobs}
        enableStickyHeader
        enableBottomToolbar
        enablePagination
        manualPagination
        rowCount={totalCount}
        onPaginationChange={onPaginationChange}
        state={{ pagination }}
        enableTopToolbar={false}
        enableRowSelection={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        muiTableContainerProps={{
          sx: { maxHeight: '600px' },
        }}
        muiTablePaginationProps={{
          sx: {
            color: theme.palette?.text?.primary,
          },
        }}
        muiBottomToolbarProps={{
          sx: {
            color: theme.palette?.text?.primary,
            backgroundColor: theme.palette?.background?.paper,
          },
        }}
        muiTableBodyRowProps={{
          hover: false,
          sx: {
            backgroundColor: theme.palette?.background?.paper,
          },
        }}
        muiTableHeadProps={{
          sx: {
            opacity: 1.0,
          },
        }}
        muiTableHeadRowProps={{
          sx: {
            color: theme.palette?.text?.primary,
            backgroundColor: theme.palette?.background?.paper,
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            textAlign: 'center',
            fontWeight: 'light',
            fontSize: '14px',
            color: theme.palette?.text?.disabled,
            borderBottom: 0,
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '0.8rem',
            color: theme.palette?.text?.primary,
            borderBottom: `1px solid ${theme.palette?.divider}`,
          },
        }}
        muiTableHeadCellColumnActionsButtonProps={{
          sx: {
            color: theme.palette?.text?.primary,
          },
        }}
      />
    </div>
  );
};

export default JobsTable;
